import {
  AbilityId,
  Buff,
  Dashing,
  Falling,
  Launch,
  PlayerTag,
  Transform,
  Velocity,
} from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import { WAVE } from '@/game/abilities/definitions'
import { signatureFor } from '@/game/abilities/signatures'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, MatchResource, PLAYER_COLORS } from '@/game/resources'
import type { Renderer } from '@/gfx/Renderer'

/**
 * Translates gameplay events into visuals.
 *
 * Nothing in the simulation knows this system exists — it subscribes to the
 * same event queues the audio and HUD read, which is why a new ability needs no
 * changes here beyond an extra case if it wants a bespoke effect.
 */
export function createVfxSystem(renderer: Renderer): SystemFn {
  return ({ world, dt }) => {
    const events = world.resource(EventsResource)
    const arena = world.resource(ArenaResource)
    const vfx = renderer.vfx

    renderer.updateArena(arena, dt)

    // Continuous trails: haste, and anyone currently being thrown.
    for (const e of world.query([PlayerTag, Transform, Velocity]).entities()) {
      if (world.has(e, Falling)) continue
      const tag = world.read(e, PlayerTag)
      const tr = world.read(e, Transform)
      const vel = world.read(e, Velocity)
      const speed = Math.hypot(vel.x, vel.z)
      const hasted = (world.get(e, Buff)?.haste ?? 0) > 0

      // Being thrown outranks being hasted: whoever is in the air gets the
      // ribbon in their own colour, so a launch is never mistaken for a run.
      //
      // The test is the launch *state*, not a speed threshold. Haste tops out
      // at 10.85 m/s, which is over any sensible threshold, so a speed test
      // gave every hasted runner the launch ribbon and the gold one — the whole
      // point of the pickup's feedback — never appeared at all.
      if (world.has(e, Launch)) {
        vfx.trail(e, tr.x, tr.z, tag.color, 1.3, Math.min(1.4, speed / 14))
        vfx.dust(tr.x, tr.z, 1, 0.3)
      } else if (world.has(e, Dashing)) {
        // Every committed move in the game is a `Dashing`: the shared dash, but
        // also ХОД (1.2 s), ПРОКАТ (0.85 s), ОБВАЛ, ОТРЫВ and ЗАХВАТ. They used
        // to draw one puff on the first frame and then nothing, so most of a
        // second of a fighter travelling under a committed move was invisible —
        // which is most of what "не всё видно" was about.
        //
        // Width from the speed, so the slow armoured advance and the 21 m/s
        // lunge do not read as the same event.
        vfx.trail(e, tr.x, tr.z, tag.color, 0.5 + Math.min(0.8, speed / 26), 1)
        if (speed > 12) vfx.spark(tr.x, tr.z, tag.color, 1)
      } else if (hasted && speed > 2) {
        vfx.trail(e, tr.x, tr.z, 0xffd23c, 1.1, 1)
        // A few embers on top of the band: the ribbon says "fast", the sparks
        // say "and it is a buff", which is what tells you it will run out.
        vfx.spark(tr.x, tr.z, 0xffd23c, 1)
      }
    }

    for (const hit of events.read(Events.Hit)) {
      if (hit.kind === 'bump' && hit.power < 3) continue
      const color = hit.braced ? 0xc8c2b0 : colorOf(world, hit.target)
      vfx.hit(hit.x, hit.z, hit.dirX, hit.dirZ, hit.power, color)
      if (hit.braced) vfx.ring(hit.x, hit.z, 0.3, 1.5, 0xc8c2b0, 0.3, 0.7, 0.3)

      // A launch is the headline event of the whole game and has to be
      // impossible to confuse with a shove: a second, wider ring in the
      // direction of travel, and a white flash when the trajectory ends over
      // the void — the moment the round is effectively decided.
      if (hit.launched) {
        vfx.ring(hit.x, hit.z, 1, 5.5 + hit.power * 0.12, 0xffffff, 0.34, 0.55, 0.06)
        vfx.cone(hit.x, hit.z, Math.atan2(hit.dirX, hit.dirZ), 4.5, color, 20)
      }
      if (hit.lethal) {
        vfx.ring(hit.x, hit.z, 0.6, 11, 0xfff2c8, 0.5, 0.85, 0.34)
        vfx.spark(hit.x, hit.z, 0xffffff, 26)
      }
    }

    // Each skip of a launched fighter off the slab: the count is how the
    // strength of a hit reads once the impact frame is gone.
    for (const bounce of events.read(Events.Bounce)) {
      vfx.skip(bounce.x, bounce.z, bounce.speed, colorOf(world, bounce.entity), bounce.index)
    }

    for (const bolt of events.read(Events.BoltBounce)) {
      const color = PLAYER_COLORS[bolt.player]
      switch (bolt.kind) {
        case 'floor':
          vfx.spark(bolt.x, bolt.z, color, 8)
          vfx.dust(bolt.x, bolt.z, 5, 0.4)
          break
        case 'prop':
          // A ricochet is a tool, so it gets a ring: you should be able to
          // register it from the corner of your eye and go for it on purpose.
          vfx.spark(bolt.x, bolt.z, 0xffffff, 16)
          vfx.ring(bolt.x, bolt.z, 0.2, 2.2, color, 0.26, 0.75, 0.2)
          break
        case 'expire':
          vfx.whiff(bolt.x, bolt.z, 0, color)
          break
      }
    }

    for (const ability of events.read(Events.Ability)) {
      // Pressing an ability that is not ready is an event of its own — a dry
      // click and a flash on the icon. It was declared in the event type from
      // the first build and never once emitted, so mashing a spent ability
      // produced total silence and read as the game ignoring the input.
      if (ability.phase === 'fail') {
        if (world.resource(MatchResource).controllers[ability.player] === 'human') {
          vfx.whiff(ability.x, ability.z, ability.angle, 0x8a8578)
        }
        continue
      }
      if (ability.phase !== 'fire') continue
      const color = PLAYER_COLORS[ability.player]

      // Slots Q and R are personal. Their effect is drawn from the `Signature`
      // event below, which knows what actually happened; the slot-shaped
      // default would draw an eight-metre shockwave over a two-metre scoop.
      if (signatureFor(world, ability.entity, ability.ability)) continue

      switch (ability.ability) {
        case AbilityId.Push: {
          // Just the muzzle. Everything the shove does now happens where the
          // bolt lands, and a cast that draws a full ring at the caster's feet
          // would claim credit for a hit that has not happened yet.
          const fx = ability.x + Math.sin(ability.angle) * 0.8
          const fz = ability.z + Math.cos(ability.angle) * 0.8
          vfx.spark(fx, fz, color, 12)
          vfx.cone(ability.x, ability.z, ability.angle, 2.2, color, 10)
          vfx.dust(ability.x, ability.z, 8, 0.7)
          break
        }
        case AbilityId.Dash:
          vfx.ring(ability.x, ability.z, 0.25, 2.6, color, 0.34, 0.9)
          vfx.cone(ability.x, ability.z, ability.angle, 2.6, color, 18)
          vfx.dust(ability.x, ability.z, 20, 0.8)
          break
        case AbilityId.Wave:
          // Two rings a beat apart read as a wave front rather than a circle.
          vfx.ring(ability.x, ability.z, 0.6, WAVE.maxRadius, color, 0.55, 1)
          vfx.ring(ability.x, ability.z, 0.3, WAVE.maxRadius * 0.7, 0xffffff, 0.34, 0.75)
          vfx.burst(ability.x, ability.z, WAVE.maxRadius * 0.5, color)
          vfx.dust(ability.x, ability.z, 34, 2)
          break
        case AbilityId.Brace:
          vfx.ring(ability.x, ability.z, 2.2, 1, 0xdcd6c4, 0.4, 1)
          vfx.dust(ability.x, ability.z, 12, 0.7)
          break
        case AbilityId.Vortex:
          vfx.ring(ability.x, ability.z, 0.3, 2, 0xb066ff, 0.4, 0.9)
          break
        default:
          break
      }
    }

    // ── the personal moves ────────────────────────────────────────────────
    //
    // Three rules, and every one of them comes from a move that was unreadable
    // without it:
    //
    // 1. The effect is the size of the move. A ring wider than the reach is a
    //    promise the move cannot keep, and the player learns to distrust all of
    //    them.
    // 2. A move with duration gets an effect with duration. ХОД runs 1.2 s and
    //    ПРОКАТ 0.85 s; one puff on the first frame leaves a second of a fighter
    //    sliding across the arena with nothing on him. That is the ribbon in the
    //    Dashing loop above.
    // 3. Anything that happens somewhere else is drawn there too — a teleport
    //    with one effect reads as a body deleted.
    for (const sig of events.read(Events.Signature)) {
      const color = PLAYER_COLORS[sig.player]
      switch (sig.move) {
        case 'barrier':
          // Slab dropping: dust where it lands, and a line across its face so
          // the cover is legible before it has finished appearing.
          vfx.dust(sig.toX, sig.toZ, 22, 1.6)
          vfx.cone(sig.toX, sig.toZ, sig.angle + Math.PI / 2, 2.6, color, 10)
          vfx.cone(sig.toX, sig.toZ, sig.angle - Math.PI / 2, 2.6, color, 10)
          vfx.ring(sig.toX, sig.toZ, 0.4, 2.4, color, 0.3, 0.8, 0.22)
          break
        case 'charge':
          // Armour, not speed: a heavy ring at the feet plus the ribbon that
          // runs for the whole advance.
          vfx.ring(sig.x, sig.z, 0.4, 2.8, color, 0.45, 0.85, 0.3)
          vfx.dust(sig.x, sig.z, 18, 1)
          break
        case 'toss': {
          // The one move whose point is *up*, so it gets the only vertical
          // effect in the set — a column, at arm's length, not a ground ring.
          const fx = sig.x + Math.sin(sig.angle) * 1.3
          const fz = sig.z + Math.cos(sig.angle) * 1.3
          // The scoop is a wide short cone (±1.22 rad, 2.4 m) — drawn as such,
          // because a fighter needs to learn how close "close enough" is.
          vfx.wedge(sig.x, sig.z, sig.angle, 1.22, sig.radius, color, 0.3, 0.4)
          vfx.burst(fx, fz, sig.radius * 0.8, color)
          vfx.dust(fx, fz, 14, 0.9)
          if (sig.hit) vfx.ring(fx, fz, 0.3, sig.radius, 0xffffff, 0.3, 0.8, 0.2)
          else vfx.whiff(sig.x, sig.z, sig.angle, color)
          break
        }
        case 'lunge':
          // Narrow: it closes a line, not an area.
          vfx.wedge(sig.x, sig.z, sig.angle, 0.34, sig.radius, color, 0.24, 0.5)
          vfx.cone(sig.x, sig.z, sig.angle, sig.radius, color, 16)
          vfx.spark(sig.x + Math.sin(sig.angle), sig.z + Math.cos(sig.angle), color, 10)
          break
        case 'gust': {
          // Pale and airy on purpose. It is the one hit in the game that does
          // not stun, and it must not borrow the impact language — no ring, no
          // white flash, just the fan itself, at the width and reach the cone
          // actually has (±0.48 rad, 6.5 m).
          const air = 0xdff2ff
                    // 0.42 painted a solid white slab over whoever was inside it, which
          // trades one unreadable frame for another.
          vfx.wedge(sig.x, sig.z, sig.angle, 0.48, sig.radius, air, 0.4, 0.3)
          vfx.cone(sig.x, sig.z, sig.angle, sig.radius, air, 30)
          vfx.dust(
            sig.x + Math.sin(sig.angle) * sig.radius * 0.5,
            sig.z + Math.cos(sig.angle) * sig.radius * 0.5,
            26,
            sig.radius * 0.45,
          )
          break
        }
        case 'retreat':
          // Kick-off at the feet, and the landing marked ahead of the body so
          // the hop reads as distance covered rather than a shove received.
          vfx.dust(sig.x, sig.z, 20, 0.8)
          vfx.cone(sig.x, sig.z, Math.atan2(sig.toX - sig.x, sig.toZ - sig.z), 2.4, color, 12)
          vfx.ring(sig.toX, sig.toZ, 1.8, 0.5, color, 0.35, 0.7, 0.22)
          break
        case 'crate':
          vfx.spark(sig.toX, sig.toZ, color, 10)
          vfx.dust(sig.toX, sig.toZ, 10, 0.7)
          break
        case 'slam':
          // The heaviest thing in the roster: two rings and a lot of floor.
          vfx.ring(sig.x, sig.z, 0.5, sig.radius, color, 0.5, 1, 0.3)
          vfx.ring(sig.x, sig.z, 0.3, sig.radius * 0.6, 0xffffff, 0.3, 0.8, 0.22)
          vfx.dust(sig.x, sig.z, 40, 2.4)
          break
        case 'swap':
          // Both ends, same instant, same colour: that pairing is the only
          // thing that says "these two positions traded" rather than "someone
          // disappeared".
          vfx.ring(sig.x, sig.z, 0.3, sig.radius, 0xb066ff, 0.4, 0.95, 0.28)
          vfx.ring(sig.toX, sig.toZ, 0.3, sig.radius, 0xb066ff, 0.4, 0.95, 0.28)
          vfx.spark(sig.x, sig.z, 0xb066ff, 16)
          vfx.spark(sig.toX, sig.toZ, 0xb066ff, 16)
          vfx.dust(sig.toX, sig.toZ, 12, 0.9)
          break
        case 'roll':
          vfx.ring(sig.x, sig.z, 0.4, 2.2, color, 0.3, 0.75, 0.24)
          vfx.dust(sig.x, sig.z, 16, 0.9)
          break
        case 'wave':
          // Two rings a beat apart read as a front rather than a circle.
          vfx.ring(sig.x, sig.z, 0.6, sig.radius, color, 0.55, 1)
          vfx.ring(sig.x, sig.z, 0.3, sig.radius * 0.7, 0xffffff, 0.34, 0.75)
          vfx.burst(sig.x, sig.z, sig.radius * 0.5, color)
          vfx.dust(sig.x, sig.z, 34, 2)
          break
        case 'anomaly':
          // Only the seed: the field draws itself for its whole life, and a
          // full-radius ring here would announce a pull that has not begun.
          vfx.ring(sig.x, sig.z, 0.3, 2, 0xb066ff, 0.4, 0.9)
          break
      }
    }

    for (const explosion of events.read(Events.Explosion)) {
      vfx.explosion(
        explosion.x,
        explosion.z,
        explosion.radius,
        explosion.kind === 'vortex' ? 0xb066ff : 0xffa23c,
      )
    }

    for (const hazard of events.read(Events.Hazard)) {
      if (hazard.kind === 'burnerFire') {
        vfx.burst(hazard.x, hazard.z, hazard.radius)
      } else {
        vfx.ring(hazard.x, hazard.z, hazard.radius, hazard.radius * 0.3, 0x8fff3c, 0.75, 0.5)
      }
    }

    for (const pickup of events.read(Events.Pickup)) {
      const color = pickup.kind === 'shield' ? 0x7fe6ff : 0xffd23c
      vfx.ring(pickup.x, pickup.z, 2.4, 0.4, color, 0.4, 1)
      vfx.burst(pickup.x, pickup.z, 2, color)
    }

    for (const fall of events.read(Events.Fall)) {
      const color = fall.player === null ? 0x6b7355 : PLAYER_COLORS[fall.player]
      vfx.ring(fall.x, fall.z, 0.4, 3.2, color, 0.6, 0.8)
      vfx.dust(fall.x, fall.z, 18, 1)
    }

    for (const collapse of events.read(Events.ArenaCollapse)) {
      if (collapse.starting) {
        vfx.ring(
          arena.def.centerX,
          arena.def.centerZ,
          arena.referenceRadius,
          arena.referenceRadius * 0.9,
          0xff3a1a,
          1.2,
          0.5,
        )
      }
    }
  }
}

function colorOf(world: Parameters<SystemFn>[0]['world'], entity: number): number {
  const tag = world.get(entity, PlayerTag)
  if (tag) return tag.color
  return 0xd8b070
}
